import React from 'react'
import { LineChart } from '@mui/x-charts'
import { Empty, Flex, Radio, Spin } from 'antd'
import Title from 'antd/es/typography/Title'
import dayjs from 'dayjs'
import { t } from 'i18next'
import { Color } from '../../Common/Color'

export const PERIODS = [1, 7, 30, 90, 365]
export const DEFAULT_PERIOD = 1

const CHART_HEIGHT = 400
// sur une seule journée les graduations portent l'heure, au dessus la date suffit
const SINGLE_DAY = 1

const label = (key) => t(`Admin.GepetoPage.chart.${key}`)

const format = (unit, digits = 1) => (value) => (
  value === null || value === undefined ? '' : `${Number(value).toFixed(digits)}${unit && ` ${unit}`}`
)

const line = (dataKey, color, unit, extra = {}) => ({
  dataKey,
  label: label(dataKey),
  color,
  valueFormatter: format(unit),
  showMark: false,
  connectNulls: true,
  ...extra
})

export const PeriodSelector = ({ period, setPeriod }) => (
  <Radio.Group
    value={period}
    onChange={({ target: { value } }) => setPeriod(value)}
    options={PERIODS.map((days) => ({ label: label(`period.${days}`), value: days }))}
    optionType='button'
    buttonStyle='solid'
  />
)

const MetricChart = ({ title, points, series, yAxis, period, setPeriod, loading }) => {
  const dataset = (points || []).map((point) => ({ ...point, date: new Date(point.date) }))
  const tickFormat = period <= SINGLE_DAY ? 'HH:mm' : 'DD/MM'
  // les graduations restent lisibles, l'infobulle donne toujours l'heure exacte du point
  const formatDate = (date, context = {}) => (
    dayjs(date).format(context.location === 'tick' ? tickFormat : 'DD/MM HH:mm')
  )

  return (
    <Flex vertical gap='1em'>
      <Flex justify='space-between' align='center' wrap gap='1em'>
        <Title level={5} style={{ margin: 0 }}>{title}</Title>
        <PeriodSelector period={period} setPeriod={setPeriod} />
      </Flex>
      <Spin spinning={loading}>
        {dataset.length === 0 && !loading && <Empty description={label('empty')} />}
        {dataset.length > 0 &&
          <LineChart
            dataset={dataset}
            xAxis={[{ dataKey: 'date', scaleType: 'time', valueFormatter: formatDate }]}
            yAxis={yAxis}
            series={series}
            height={CHART_HEIGHT}
          />
        }
      </Spin>
    </Flex>
  )
}

export const SpeedtestChart = ({ points, ...props }) => (
  <MetricChart
    title={label('speedtest')}
    points={points}
    yAxis={[{ label: 'Mbps', min: 0 }]}
    series={[
      line('download', Color.primary_blue, 'Mbps'),
      line('upload', Color.green, 'Mbps')
    ]}
    {...props}
  />
)

export const PingChart = ({ points, ...props }) => (
  <MetricChart
    title={label('ping')}
    points={points}
    yAxis={[
      { id: 'latency', label: 'ms', min: 0 },
      { id: 'loss', position: 'right', label: '%', min: 0 }
    ]}
    series={[
      line('rtt_avg', Color.primary_blue, 'ms', { yAxisId: 'latency' }),
      line('rtt_mdev', Color.dark_orange, 'ms', { yAxisId: 'latency' }),
      line('packet_loss', Color.red, '%', { yAxisId: 'loss' })
    ]}
    {...props}
  />
)

export const SessionChart = ({ points, ...props }) => (
  <MetricChart
    title={label('session')}
    points={points}
    yAxis={[{ min: 0 }]}
    series={[line('sessions', Color.primary_blue, '', { valueFormatter: format('', 0) })]}
    {...props}
  />
)

// chutes de tension signalées par le noyau du Pi entre deux relevés (moyenne par relevé au delà d'un mois)
export const PowerChart = ({ points, ...props }) => (
  <MetricChart
    title={label('power')}
    points={points}
    yAxis={[{ min: 0 }]}
    series={[line('under_voltage_events', Color.red, '', { valueFormatter: format('', 0) })]}
    {...props}
  />
)

export const SystemChart = ({ points, ...props }) => (
  <MetricChart
    title={label('system')}
    points={points}
    yAxis={[
      // mémoire, disque (%) et température (°C) partagent la même échelle 0-100
      { id: 'percent', label: '% / °C', min: 0, max: 100 },
      { id: 'load', position: 'right', label: label('load'), min: 0 }
    ]}
    series={[
      line('mem', Color.primary_blue, '%', { yAxisId: 'percent' }),
      line('disk', Color.green, '%', { yAxisId: 'percent' }),
      line('temperature', Color.red, '°C', { yAxisId: 'percent' }),
      line('load', Color.dark_orange, '', { yAxisId: 'load', valueFormatter: format('', 2) })
    ]}
    {...props}
  />
)
